import { memo, useEffect, useState } from 'react'
import { cn } from '@trafiflow/shared-ui'
import {
  X,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  AlertTriangle,
  ChevronRight,
} from 'lucide-react'
import type { AdGenStageStatus, AdGenStageDetail, StageStatus } from '@/services/ad-generator/types'
import { adGeneratorService } from '@/services/ad-generator'

interface StageDetailPanelProps {
  stage: AdGenStageStatus | null
  jobId: string
  onClose: () => void
  className?: string
}

interface StatusConfigItem {
  icon: typeof Clock
  color: string
  label: string
  animate?: boolean
}

const STATUS_CONFIG: Record<StageStatus, StatusConfigItem> = {
  pending: { icon: Clock, color: 'text-tf-text-muted', label: 'Pending' },
  running: { icon: Loader2, color: 'text-blue-400', label: 'Running', animate: true },
  completed: { icon: CheckCircle, color: 'text-emerald-400', label: 'Completed' },
  degraded: { icon: AlertTriangle, color: 'text-amber-400', label: 'Degraded' },
  failed: { icon: XCircle, color: 'text-red-400', label: 'Failed' },
  skipped: { icon: ChevronRight, color: 'text-tf-text-muted', label: 'Skipped' },
}

function StageDetailPanelComponent({
  stage,
  jobId,
  onClose,
  className,
}: StageDetailPanelProps) {
  const [detail, setDetail] = useState<AdGenStageDetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!stage || (stage.status !== 'completed' && stage.status !== 'degraded')) {
      setDetail(null)
      return
    }

    const fetchDetail = async () => {
      setLoading(true)
      setError(null)
      try {
        const result = await adGeneratorService.getStageDetail(jobId, stage.name)
        setDetail(result)
      } catch (err) {
        setError('Failed to load stage details')
        console.error('[StageDetailPanel] Error:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchDetail()
  }, [stage, jobId])

  if (!stage) return null

  const config = STATUS_CONFIG[stage.status] || STATUS_CONFIG.pending
  const Icon = config.icon

  return (
    <div
      className={cn(
        'fixed right-0 top-0 h-full w-96 bg-tf-bg-card border-l border-tf-border',
        'shadow-2xl z-50 flex flex-col',
        'animate-in slide-in-from-right duration-200',
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-tf-border">
        <div className="flex items-center gap-2">
          <Icon
            className={cn(
              'w-5 h-5',
              config.color,
              config.animate && 'animate-spin',
            )}
          />
          <h3 className="font-semibold text-tf-text">{stage.display_name}</h3>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded hover:bg-tf-bg-muted transition-colors"
        >
          <X className="w-4 h-4 text-tf-text-muted" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 space-y-4">
        {/* Status badge */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-tf-text-muted">Status:</span>
          <span className={cn('text-xs font-medium', config.color)}>
            {config.label}
          </span>
        </div>

        {/* Duration */}
        {stage.duration_ms && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-tf-text-muted">Duration:</span>
            <span className="text-xs font-medium text-tf-text">
              {(stage.duration_ms / 1000).toFixed(2)}s
            </span>
          </div>
        )}

        {/* ETA */}
        {stage.status === 'pending' && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-tf-text-muted">Estimated:</span>
            <span className="text-xs font-medium text-tf-text">
              ~{stage.eta_seconds}s
            </span>
          </div>
        )}

        {/* Insight */}
        {stage.insight && (
          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <p className="text-xs text-blue-400">{stage.insight}</p>
          </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
          </div>
        )}

        {/* Error state */}
        {error && (
          <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
            <p className="text-xs text-red-400">{error}</p>
          </div>
        )}

        {/* Artifact data */}
        {detail?.artifact && (
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-tf-text-muted uppercase tracking-wider">
              Artifact Data
            </h4>
            <div className="p-3 rounded-lg bg-tf-bg-muted border border-tf-border overflow-hidden">
              <pre className="text-[10px] text-tf-text-muted overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(detail.artifact, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-tf-border">
        <button
          onClick={onClose}
          className="w-full px-4 py-2 text-xs font-medium text-tf-text bg-tf-bg-muted rounded-lg hover:bg-tf-border transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  )
}

export default memo(StageDetailPanelComponent)
